import React, { useMemo } from "react";
import {
  buildWorker360Metrics,
  getWorker360ShiftHours,
  getWorker360ShiftStart,
  getWorker360ShiftEnd,
  worker360ToDate,
  normalizeWorker360Value,
} from "../utils/worker360Metrics";

function normalize(value) {
  return String(value || "").trim().toLowerCase();
}


function formatDate(value) {
  const date = worker360ToDate(value);

  if (!date) return "Date unavailable";

  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatTime(value) {
  const date = worker360ToDate(value);

  if (!date) return "—";

  return date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

function titleCase(value) {
  return String(value || "unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function MetricCard({ label, value, color = "#0f172a" }) {
  return (
    <div
      style={{
        padding: 13,
        borderRadius: 14,
        border: "1px solid #e2e8f0",
        background: "#f8fafc",
      }}
    >
      <div
        style={{
          color: "#64748b",
          fontSize: 10,
          fontWeight: 900,
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 6,
          color,
          fontSize: 21,
          fontWeight: 950,
        }}
      >
        {value}
      </div>
    </div>
  );
}

export default function Worker360Shifts({
  worker,
  shifts = [],
}) {
  const shiftMetrics = useMemo(
    () => buildWorker360Metrics(worker, shifts),
    [worker, shifts]
  );

  const {
    workerShifts,
    completedShifts,
    activeShifts,
    noShowShifts,
    workerCancelledShifts,
    lateArrivalShifts,
    attendance: attendancePercent,
    reliability: reliabilityPercent,
    totalHours,
  } = shiftMetrics;

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div>
        <div
          style={{
            color: "#0f172a",
            fontSize: 20,
            fontWeight: 950,
          }}
        >
          Shift History
        </div>

        <div
          style={{
            marginTop: 4,
            color: "#64748b",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          Actual shift assignments and completed work for this worker.
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(2, minmax(0, 1fr))",
          gap: 9,
        }}
      >
        <MetricCard
          label="All Shifts"
          value={workerShifts.length}
        />

        <MetricCard
          label="Completed"
          value={completedShifts.length}
          color="#166534"
        />

        <MetricCard
          label="Active"
          value={activeShifts.length}
          color="#1d4ed8"
        />

        <MetricCard
          label="Hours Worked"
          value={totalHours.toFixed(1)}
          color="#7c3aed"
        />
      </div>

<div
  style={{
    padding: 14,
    borderRadius: 16,
    border: "1px solid #dbeafe",
    background: "#eff6ff",
  }}
>
  <div
    style={{
      color: "#1e3a8a",
      fontSize: 14,
      fontWeight: 950,
    }}
  >
    Attendance & Reliability
  </div>

  <div
    style={{
      marginTop: 10,
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 8,
    }}
  >
    <MetricCard
      label="Attendance"
      value={
        attendancePercent !== null
          ? `${attendancePercent}%`
          : "—"
      }
      color="#166534"
    />

    <MetricCard
      label="Reliability"
      value={
        reliabilityPercent !== null
          ? reliabilityPercent
          : "—"
      }
      color="#1d4ed8"
    />

    <MetricCard
      label="No-Shows"
      value={noShowShifts.length}
      color={
        noShowShifts.length > 0 ? "#b91c1c" : "#166534"
      }
    />

    <MetricCard
      label="Late Arrivals"
      value={lateArrivalShifts.length}
      color={
        lateArrivalShifts.length > 0
          ? "#b45309"
          : "#166534"
      }
    />

    <MetricCard
      label="Worker Cancellations"
      value={workerCancelledShifts.length}
      color={
        workerCancelledShifts.length > 0
          ? "#b91c1c"
          : "#166534"
      }
    />
  </div>
</div>

      {workerShifts.length === 0 ? (
        <div
          style={{
            padding: 20,
            borderRadius: 14,
            textAlign: "center",
            color: "#64748b",
            background: "#f8fafc",
            border: "1px dashed #cbd5e1",
            fontWeight: 750,
          }}
        >
          No shift records were found for this worker.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 9 }}>
          {workerShifts.map((shift) => {
            const start = getWorker360ShiftStart(shift);
            const end = getWorker360ShiftEnd(shift);
            const status = normalize(shift.status);

            return (
              <div
                key={shift.id || shift.shiftId}
                style={{
                  padding: 13,
                  borderRadius: 14,
                  background: "#ffffff",
                  border: "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 10,
                  }}
                >
                  <div>
                    <div
                      style={{
                        color: "#0f172a",
                        fontSize: 14,
                        fontWeight: 950,
                      }}
                    >
                      {shift.facilityName ||
                        shift.facilityId ||
                        "Facility"}
                    </div>

                    <div
                      style={{
                        marginTop: 4,
                        color: "#64748b",
                        fontSize: 12,
                        fontWeight: 750,
                      }}
                    >
                      {formatDate(start)}
                    </div>

                    <div
                      style={{
                        marginTop: 3,
                        color: "#475569",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {formatTime(start)} – {formatTime(end)}
                      {" · "}
                      {getWorker360ShiftHours(shift).toFixed(1)} hours
                    </div>
                  </div>

                  <div
                    style={{
                      padding: "6px 9px",
                      borderRadius: 999,
                      color:
                        status === "completed"
                          ? "#166534"
                          : status === "cancelled" ||
                            status === "canceled" ||
                            status === "no_show"
                          ? "#991b1b"
                          : "#1d4ed8",
                      background:
                        status === "completed"
                          ? "#dcfce7"
                          : status === "cancelled" ||
                            status === "canceled" ||
                            status === "no_show"
                          ? "#fee2e2"
                          : "#dbeafe",
                      fontSize: 10,
                      fontWeight: 950,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {titleCase(status)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}